/**
 * UTM Tracking System
 * Handles initial referrer capture, Axeptio consent surveillance,
 * and dynamic propagation of UTM data via dataLayer and cookies.
 */

enum TrackingValue {
  DIRECT = 'direct',
  NOT_SET = '(not set)',
  ORGANIC = 'Organic Search',
  REFERRAL = 'Referral',
  GEO = 'geo'
}

enum TrafficSource {
  GOOGLE = 'google',
  BING = 'bing',
  YAHOO = 'yahoo',
  ECOSIA = 'ecosia',
  DUCKDUCKGO = 'duckduckgo',
  QWANT = 'qwant',
  CHATGPT = 'chatgpt',
  PERPLEXITY = 'perplexity',
  GEMINI = 'gemini',
  CLAUDE = 'claude'
}

enum UTMParameter {
  SOURCE = 'utm_source',
  MEDIUM = 'utm_medium',
  CAMPAIGN = 'utm_campaign',
  TERM = 'utm_term',
  CONTENT = 'utm_content',
  GCLID = 'gclid',
  FBCLID = 'fbclid',
  INITIAL_REFERRER = 'initial_referrer'
}

const ORGANIC_SOURCES = [
  TrafficSource.GOOGLE,
  TrafficSource.BING,
  TrafficSource.YAHOO,
  TrafficSource.ECOSIA,
  TrafficSource.DUCKDUCKGO,
  TrafficSource.QWANT,
] as const;

const AI_SOURCES = [
  TrafficSource.CHATGPT,
  TrafficSource.PERPLEXITY,
  TrafficSource.GEMINI,
  TrafficSource.CLAUDE,
] as const;

const COOKIE_DURATION: number = 90;

const getCookie = (name: string): string => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const rawValue = parts.pop()?.split(';').shift() || '';
    return rawValue === TrackingValue.NOT_SET ? rawValue : decodeURIComponent(rawValue);
  }

  return '';
};

const setCookie = (
  name: string,
  value: string,
  days: number = COOKIE_DURATION,
): void => {
  const date = new Date();

  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));

  const cookieValue = value === TrackingValue.NOT_SET ? value : encodeURIComponent(value);
  document.cookie = `${name}=${cookieValue};expires=${date.toUTCString()};path=/;SameSite=Lax`;
};

const setUTMCookie = (name: string, value: string): void => {
  if (!value) {
    return;
  }

  setCookie(name, value, COOKIE_DURATION);
};

const getGAID = (): string => {
  const ga = getCookie('_ga');

  if (!ga) {
    return '';
  }

  const parts = ga.split('.');

  if (parts.length < 4) {
    return '';
  }

  return `${parts[2]}.${parts[3]}`;
};

export const getUtmData = (): Record<string, string | null> => ({
  utm_source: getCookie(UTMParameter.SOURCE) || sessionStorage.getItem(UTMParameter.SOURCE) || null,
  utm_medium: getCookie(UTMParameter.MEDIUM) || sessionStorage.getItem(UTMParameter.MEDIUM) || null,
  utm_campaign: getCookie(UTMParameter.CAMPAIGN) || sessionStorage.getItem(UTMParameter.CAMPAIGN) || null,
  utm_term: getCookie(UTMParameter.TERM) || sessionStorage.getItem(UTMParameter.TERM) || null,
  utm_content: getCookie(UTMParameter.CONTENT) || sessionStorage.getItem(UTMParameter.CONTENT) || null,
  gclid: getCookie('gclid_Cookies') || sessionStorage.getItem(UTMParameter.GCLID) || null,
  fbclid: getCookie('fbclid_Cookies') || sessionStorage.getItem(UTMParameter.FBCLID) || null,
});

const detectFromReferrer = (referrer: string): void => {
  if (!referrer || referrer === TrackingValue.DIRECT) {
    setCookie(UTMParameter.SOURCE, TrackingValue.NOT_SET);
    setCookie(UTMParameter.MEDIUM, TrackingValue.DIRECT.charAt(0).toUpperCase() + TrackingValue.DIRECT.slice(1));
    setCookie(UTMParameter.CAMPAIGN, TrackingValue.NOT_SET);

    return;
  }

  const organicRegex = new RegExp(ORGANIC_SOURCES.join('|'));
  const aiRegex = new RegExp(AI_SOURCES.join('|'));

  if (organicRegex.test(referrer)) {
    const source = referrer.match(organicRegex)?.[0] || 'organic';

    setCookie(UTMParameter.SOURCE, source);
    setCookie(UTMParameter.MEDIUM, TrackingValue.ORGANIC);
    setCookie(UTMParameter.CAMPAIGN, TrackingValue.NOT_SET);

    return;
  }

  if (aiRegex.test(referrer)) {
    const source = referrer.match(aiRegex)?.[0] || 'ai';

    setCookie(UTMParameter.SOURCE, source);
    setCookie(UTMParameter.MEDIUM, TrackingValue.GEO);
    setCookie(UTMParameter.CAMPAIGN, TrackingValue.NOT_SET);

    return;
  }

  try {
    const host = new URL(referrer).hostname;

    setCookie(UTMParameter.SOURCE, host);
    setCookie(UTMParameter.MEDIUM, TrackingValue.REFERRAL);
    setCookie(UTMParameter.CAMPAIGN, TrackingValue.NOT_SET);
  } catch (e: unknown) {
    setCookie(UTMParameter.SOURCE, TrackingValue.NOT_SET);
    setCookie(UTMParameter.MEDIUM, TrackingValue.DIRECT.charAt(0).toUpperCase() + TrackingValue.DIRECT.slice(1));
    setCookie(UTMParameter.CAMPAIGN, TrackingValue.NOT_SET);
  }
};

const createCookiesUTM = (): void => {
  const urlParams = new URLSearchParams(window.location.search);

  const getParam = (key: string): string => urlParams.get(key) || sessionStorage.getItem(key) || '';

  setUTMCookie('gclid_Cookies', getParam(UTMParameter.GCLID));
  setUTMCookie('fbclid_Cookies', getParam(UTMParameter.FBCLID));

  if (
    getCookie(UTMParameter.SOURCE)
    || getCookie(UTMParameter.MEDIUM)
    || getCookie(UTMParameter.CAMPAIGN)
  ) {
    return;
  }

  const utmSource = getParam(UTMParameter.SOURCE);
  const utmMedium = getParam(UTMParameter.MEDIUM);
  const utmCampaign = getParam(UTMParameter.CAMPAIGN);

  if (utmSource || utmMedium || utmCampaign) {
    setCookie(UTMParameter.SOURCE, utmSource || '');
    setCookie(UTMParameter.MEDIUM, utmMedium || '');
    setCookie(UTMParameter.CAMPAIGN, utmCampaign || '');
    setCookie(UTMParameter.TERM, getParam(UTMParameter.TERM) || '');
    setCookie(UTMParameter.CONTENT, getParam(UTMParameter.CONTENT) || '');

    return;
  }

  const referrer = sessionStorage.getItem(UTMParameter.INITIAL_REFERRER) || document.referrer || '';

  detectFromReferrer(referrer);
};

const processUTMCookies = (): boolean => {
  const axeptioCookies = getCookie('axeptio_cookies');

  if (!axeptioCookies) {
    return false;
  }

  try {
    const consentData = JSON.parse(decodeURIComponent(axeptioCookies));

    if (consentData.google_analytics !== true) {
      return false;
    }

    createCookiesUTM();

    return true;
  } catch (e: unknown) {
    return false;
  }
};

const captureInitialData = (): void => {
  if (!sessionStorage.getItem(UTMParameter.INITIAL_REFERRER)) {
    const referrer = document.referrer || '';

    if (referrer && !referrer.includes(window.location.hostname)) {
      sessionStorage.setItem(UTMParameter.INITIAL_REFERRER, referrer);
    } else if (!referrer) {
      sessionStorage.setItem(UTMParameter.INITIAL_REFERRER, TrackingValue.DIRECT);
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmParameters: UTMParameter[] = [
    UTMParameter.SOURCE,
    UTMParameter.MEDIUM,
    UTMParameter.CAMPAIGN,
    UTMParameter.TERM,
    UTMParameter.CONTENT,
    UTMParameter.GCLID,
    UTMParameter.FBCLID,
  ];

  utmParameters.forEach((utmParameter) => {
    const value = urlParams.get(utmParameter);

    if (!value || sessionStorage.getItem(utmParameter)) {
      return;
    }

    sessionStorage.setItem(utmParameter, value);
  });
};

const setupAxeptioSurveillance = (): void => {
  window._axcb = window._axcb || [];

  window._axcb.push((axeptio: AxeptioSDK) => {
    axeptio.on('update', (choices: AxeptioChoices) => {
      if (choices?.$$completed) {
        setTimeout(processUTMCookies, 200);
      }
    });
  });

  const interval = setInterval(() => {
    const axeptioCookies = getCookie('axeptio_cookies');

    if (!axeptioCookies) {
      return;
    }

    const success = processUTMCookies();

    if (success) {
      clearInterval(interval);
    }
  }, 2000);
};

export const initUTMTracking = (): void => {
  captureInitialData();
  setupAxeptioSurveillance();
};
